/* Cruscotto: cosa c'e' da fare oggi, in una pagina.
 *
 * Idea raccolta il 22 settembre 2026, costruita il 23: quanto c'è da
 * fatturare, quanto aspetta di essere incassato, se la numerazione ha
 * buchi da spiegare. Ogni numero viene da una rotta che esisteva già
 * (`/api/maturato/anzianita`, `/api/documenti/avvisi-aperti`) o che esiste
 * apposta per questo (`/api/documenti/buchi`): questa pagina non calcola
 * niente da sola, aggrega quello che il dominio calcola già.
 *
 * Il 25 settembre 2026 si aggiunge l'anzianità del maturato: lo stesso
 * totale diviso per età del lavoro, più le pratiche da guardare per prime.
 * È il rapporto di base di ogni gestionale di pratica, perché un lavoro
 * fatto sei mesi fa e mai fatturato è il ricavo più facile da perdere.
 *
 * **Non è un modulo di lavoro, è una fotografia.** Comporre un avviso o
 * incassarne uno resta in `/documenti`: qui non c'è nessun modulo, solo
 * numeri e un rimando a dove si agisce.
 *
 * **Il 23 ottobre 2026 si e' riordinato** (stessa idea, meno strada):
 *
 * - *Un numero, un posto.* I quattro numeri in testa sono i totali; le sezioni sotto hanno solo gli
 *   elenchi da cui si agisce. Prima il totale degli avvisi, il conteggio delle fatture da passare e
 *   l'allarme dei 12 giorni comparivano due o tre volte.
 * - *Sei richieste e non nove.* Il da fatturare e le registrazioni senza tariffa vengono
 *   dall'anzianita' (stessa funzione del maturato, stesso totale al centesimo), e i tre elenchi interi
 *   di documenti, clienti e catalogo, letti solo per sapere se erano vuoti, sono cinque numeri di
 *   `GET /api/documenti/stato-avvio`.
 * - *Il titolare ha una sezione in piu'*: le pratiche a prezzo concordato il cui costo si avvicina al
 *   prezzo. Usa i costi orari, quindi la rotta e' sua (`RACCOLTA_METRICHE`) e chi fattura non la chiede.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { controllo, documenti, incassi } from "../api";
import { useCarica } from "../carica";
import { PrimiPassi } from "../componenti/PrimiPassi";
import { useSessione } from "../sessione";
import {
  conta,
  dataCompleta,
  eZero,
  euro,
  somma,
  type Anzianita,
  type BucoNumerazione,
  type DaPassare,
  type Documento,
  type PrezzoConcordato,
  type StatoAvvio,
  type VersamentoDaFatturare,
} from "../dominio";

/** Quante righe mostra ogni elenco del cruscotto: e' una fotografia, non un registro. Il resto sta a un
 * clic, nella scheda dove si lavora. */
const QUANTI = 8;

export function Cruscotto() {
  const { utente } = useSessione();
  const eTitolare = utente?.ruolo === "AMMINISTRATORE";
  const anzianita = useCarica<Anzianita>(() => documenti.anzianita(), []);
  const avvisiAperti = useCarica<Documento[]>(() => documenti.avvisiAperti(), []);
  const buchi = useCarica<BucoNumerazione[]>(() => documenti.buchi(), []);
  const coda = useCarica<DaPassare[]>(() => documenti.daPassare(), []);
  const aMano = useCarica<VersamentoDaFatturare[]>(() => incassi.daFatturare(), []);
  const avvio = useCarica<StatoAvvio>(() => documenti.statoAvvio(), []);
  // Solo il titolare la chiede, e un suo errore non ferma il resto della pagina: e' una sezione in piu'.
  const prezzi = useCarica<PrezzoConcordato | null>(
    () => (eTitolare ? controllo.praticheAPrezzo() : Promise.resolve(null)),
    [eTitolare],
  );

  const errore =
    anzianita.errore ?? avvisiAperti.errore ?? buchi.errore ?? coda.errore ?? aMano.errore ?? avvio.errore;
  const pronto =
    anzianita.dati !== undefined &&
    avvisiAperti.dati !== undefined &&
    buchi.dati !== undefined &&
    coda.dati !== undefined &&
    aMano.dati !== undefined &&
    avvio.dati !== undefined;
  const a = anzianita.dati;
  const s = avvio.dati;

  return (
    <>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      {!pronto || a === undefined || s === undefined ? (
        errore ? null : <p className="attesa">Un momento.</p>
      ) : (
        <>
          <p className="aiuto scheda-domanda">Cosa c'è da fare oggi.</p>
          <PrimiPassi clientiMancanti={s.clienti === 0} catalogoMancante={s.prestazioni === 0} />
          {s.documenti === 0 && s.clienti !== 0 && s.prestazioni !== 0 && (
            <p className="aiuto avvio-documenti">
              Nessun documento ancora. <Link to="/ore/giornata">Segna le ore del primo lavoro</Link>, poi prepara l'avviso da Fatturare.
            </p>
          )}
          {s.da_rivedere > 0 && (
            <p className="riga-avviso">
              {conta(s.da_rivedere, "prestazione nuova", "prestazioni nuove")} da rivedere: l'avviso che le
              contiene non si crea finché non scegli la frase per la fattura.{" "}
              <Link to="/fatturare/da-fatturare">Rivedile</Link>.
            </p>
          )}
          <NumeriInTesta
            daFatturare={a.totale}
            avvisi={avvisiAperti.dati ?? []}
            coda={coda.dati ?? []}
            aMano={aMano.dati ?? []}
          />
          <p className="aiuto cruscotto-periodo">
            Sono i totali di oggi. Per un trimestre, un semestre o un anno, e per sapere chi deve ancora pagare:{" "}
            <Link to="/controllo/periodo">guardali per periodo</Link>.
          </p>
          <div className="cruscotto-registro">
            <MaturatoNonFatturato senzaTariffa={a.senza_tariffa} />
            <AnzianitaMaturato anzianita={a} />
            {eTitolare && prezzi.dati && <PrezzoConcordatoDaGuardare prezzi={prezzi.dati} />}
            {eTitolare && prezzi.errore && (
              <p className="riga-avviso">Non sono riuscito a leggere le pratiche a prezzo concordato: {prezzi.errore}</p>
            )}
            <AvvisiAperti documenti={avvisiAperti.dati ?? []} />
            <DaPassareSuFattureSmart coda={coda.dati ?? []} aMano={aMano.dati ?? []} />
            <BuchiNumerazione buchi={buchi.dati ?? []} numerati={s.documenti_numerati} />
          </div>
        </>
      )}
    </>
  );
}

/** I quattro numeri che si guardano per primi, grandi e in alto, ognuno un
 * collegamento a dove si agisce. Il resto della pagina e' il dettaglio. Il
 * quarto si accende solo quando c'e' qualcosa di scaduto: un allarme che
 * resta sempre acceso smette di essere guardato. */
function NumeriInTesta({
  daFatturare,
  avvisi,
  coda,
  aMano,
}: {
  daFatturare: string;
  avvisi: Documento[];
  coda: DaPassare[];
  aMano: VersamentoDaFatturare[];
}) {
  // Quanto manca davvero: un avviso con degli acconti conta per il suo residuo.
  const daIncassare = somma(avvisi.map((d) => d.residuo ?? d.netto_a_pagare));
  const scadute = [...coda, ...aMano].filter((v) => v.urgenza === "scaduto").length;
  const dafatturareSmart = coda.length + aMano.length;
  return (
    <div className="numeri-testa">
      <Link to="/fatturare/da-fatturare" className="numero-testa">
        <span className="numero-testa-cifra">{euro(daFatturare)}</span>
        <span className="numero-testa-nome">da fatturare</span>
      </Link>
      <Link to="/fatturare/avvisi" className="numero-testa">
        <span className="numero-testa-cifra">{euro(daIncassare)}</span>
        <span className="numero-testa-nome">
          da incassare, {conta(avvisi.length, "avviso", "avvisi")}
        </span>
      </Link>
      <Link to="/fatturare/fatture-smart" className="numero-testa">
        <span className="numero-testa-cifra">{dafatturareSmart}</span>
        <span className="numero-testa-nome">
          {dafatturareSmart === 1 ? "fattura da emettere" : "fatture da emettere"} su Fatture Smart
        </span>
      </Link>
      <Link
        to="/fatturare/fatture-smart"
        className={scadute > 0 ? "numero-testa numero-testa-allarme" : "numero-testa"}
      >
        <span className="numero-testa-cifra">{scadute}</span>
        <span className="numero-testa-nome">oltre i 12 giorni dall'incasso</span>
      </Link>
    </div>
  );
}

/** Il totale da fatturare sta gia' nei numeri in testa: qui resta solo quello
 * che quel numero non dice, cioe' le ore rimaste fuori perche' senza prezzo. */
function MaturatoNonFatturato({ senzaTariffa }: { senzaTariffa: number }) {
  if (senzaTariffa === 0) return null;
  return (
    <p className="riga-avviso">
      {conta(senzaTariffa, "registrazione", "registrazioni")} senza tariffa, escluse dal totale
      da fatturare: non entrano in nessun avviso finché il listino non le copre.
    </p>
  );
}

/** Solo per le proporzioni della barra: gli importi mostrati restano le
 * stringhe esatte del server. */
function peso(importo: string): number {
  return Number.parseFloat(importo) || 0;
}

function percentuale(x: string): number {
  return Math.round(Number(x) * 100);
}

function AnzianitaMaturato({ anzianita }: { anzianita: Anzianita }) {
  const totale = peso(anzianita.totale);
  if (totale <= 0) return null;
  const conValore = anzianita.fasce.filter((f) => !eZero(f.valore));

  return (
    <section className="elenco">
      <h2>Da quanto aspetta</h2>
      <p className="aiuto">
        Lo stesso importo da fatturare, diviso per età del lavoro.
      </p>
      <div
        className="barra-anzianita"
        role="img"
        aria-label={conValore.map((f) => `${f.etichetta}: ${euro(f.valore)}`).join("; ")}
      >
        {anzianita.fasce.map((f, i) =>
          eZero(f.valore) ? null : (
            <span
              key={f.etichetta}
              className={`fascia fascia-${i}`}
              style={{ width: `${(peso(f.valore) / totale) * 100}%` }}
            />
          ),
        )}
      </div>
      <table className="tabella">
        <tbody>
          {anzianita.fasce.map((f, i) => (
            <tr key={f.etichetta}>
              <td>
                <span className={`pallino fascia-${i}`} aria-hidden="true" /> {f.etichetta}
              </td>
              <td className="aiuto">
                {f.pratiche === 0 ? "" : conta(f.pratiche, "pratica", "pratiche")}
              </td>
              <td className="cifra colonna-importo">{euro(f.valore)}</td>
            </tr>
          ))}
          {!eZero(anzianita.senza_data) && (
            <tr>
              <td>senza data</td>
              <td className="aiuto">prezzo concordato, nessuna ora e nessuna apertura</td>
              <td className="cifra colonna-importo">{euro(anzianita.senza_data)}</td>
            </tr>
          )}
        </tbody>
      </table>

      {anzianita.ferme.length > 0 && (
        <>
          <h3>Da guardare per prime</h3>
          <p className="aiuto">
            Lavoro fermo da più di sessanta giorni, o pratiche chiuse e mai fatturate:
            le {Math.min(QUANTI, anzianita.ferme.length)} più vecchie di {anzianita.ferme.length}.
          </p>
          <table className="tabella">
            <tbody>
              {anzianita.ferme.slice(0, QUANTI).map((f) => (
                <tr key={f.pratica_id}>
                  <td>
                    <Link className="pratica-nome" to={`/fatturare/da-fatturare?pratica=${encodeURIComponent(f.pratica_id)}`}>{f.etichetta}</Link>
                    <span className="narrativa">
                      {f.chiusa_il
                        ? `chiusa il ${dataCompleta(f.chiusa_il)} e non ancora fatturata`
                        : f.giorni !== null
                          ? `lavoro più vecchio di ${f.giorni} giorni`
                          : "senza data"}
                    </span>
                  </td>
                  <td className="cifra colonna-importo">{euro(f.da_fatturare)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {anzianita.ferme.length > QUANTI && (
            <p>
              <Link to="/fatturare/da-fatturare">
                Altre {anzianita.ferme.length - QUANTI}: vedile tutte in Fatturare
              </Link>
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** Le pratiche a prezzo concordato il cui costo si avvicina al prezzo: solo per il titolare. Il prezzo e'
 * pattuito e ogni ora in piu' lo erode, quindi qui si guarda mentre si e' ancora in tempo (Gestione lo
 * dice a lavoro finito). La quota e' costo / prezzo; con ore senza costo orario e' un minimo, e la riga lo dice. */
function PrezzoConcordatoDaGuardare({ prezzi }: { prezzi: PrezzoConcordato }) {
  // Otto alla volta, come il resto del cruscotto, ma qui il resto si apre: queste sono le pratiche su cui
  // si puo' ancora intervenire, e non devono restare nascoste dietro un "altre".
  const [quante, setQuante] = useState(QUANTI);
  const soglia = percentuale(prezzi.soglia_in_vista);
  const monitorate = conta(prezzi.monitorate, "pratica a prezzo concordato", "pratiche a prezzo concordato");
  return (
    <section className="elenco">
      <h2>Prezzo concordato: il costo si avvicina</h2>
      {prezzi.pratiche.length === 0 ? (
        <p className="aiuto">
          {prezzi.monitorate === 0
            ? "Nessuna pratica a prezzo concordato con ore registrate."
            : `Su ${monitorate} nessuna ha ancora consumato il ${soglia}% del prezzo.`}
        </p>
      ) : (
        <>
          <p className="aiuto">
            Su {monitorate}, {conta(prezzi.pratiche.length, "ha", "hanno")} già consumato almeno il {soglia}% del
            prezzo in costo delle ore.
          </p>
          <table className="tabella">
            <tbody>
              {prezzi.pratiche.slice(0, quante).map((p) => (
                <tr key={p.pratica_id}>
                  <td>
                    <Link className="pratica-nome" to={`/fatturare/da-fatturare?pratica=${encodeURIComponent(p.pratica_id)}`}>
                      {p.etichetta}
                    </Link>
                    <span className="narrativa">
                      {p.sforata ? "il costo ha raggiunto il prezzo" : "il costo si avvicina al prezzo"}
                      {p.senza_costo > 0 &&
                        `; almeno, manca il costo di ${conta(p.senza_costo, "registrazione", "registrazioni")}`}
                    </span>
                  </td>
                  <td className={`cifra colonna-importo${p.sforata ? " sotto-listino" : ""}`}>
                    {percentuale(p.quota)}%
                    <span className="narrativa">
                      {euro(p.costo)} su {euro(p.prezzo)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {prezzi.pratiche.length > quante && (
            <p>
              <button type="button" className="tasto" onClick={() => setQuante(quante + QUANTI)}>
                Mostra altre {Math.min(QUANTI, prezzi.pratiche.length - quante)} ({prezzi.pratiche.length - quante} ancora nascoste)
              </button>
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** Il totale e il numero di avvisi stanno nei numeri in testa: qui resta l'elenco dei piu' vecchi. */
function AvvisiAperti({ documenti: avvisi }: { documenti: Documento[] }) {
  return (
    <section className="elenco">
      <h2>Avvisi mandati, da incassare: i più vecchi</h2>
      {avvisi.length === 0 ? (
        <p className="aiuto">Nessun avviso in attesa di pagamento.</p>
      ) : (
        <>
          <table className="tabella">
            <tbody>
              {[...avvisi]
                .sort((a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : 0))
                .slice(0, QUANTI)
                .map((d) => (
                <tr key={d.identificativo}>
                  <td>
                    <Link className="pratica-nome" to={`/fatturare/avvisi?documento=${encodeURIComponent(d.identificativo)}`}>{d.intestatario || "Senza intestatario"}</Link>
                    <span className="narrativa">{dataCompleta(d.data)}</span>
                  </td>
                  <td className="cifra colonna-importo">{euro(d.residuo ?? d.netto_a_pagare)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {avvisi.length > QUANTI && (
            <p>
              <Link to="/fatturare/avvisi">Altri {avvisi.length - QUANTI}: vedili tutti in Avvisi</Link>
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** Gli incassi che aspettano la fattura su Fatture Smart. E' l'unico numero
 * del cruscotto con una scadenza di legge (12 giorni dall'incasso), quindi dice
 * anche il piu' vecchio. Quante sono e quante sono scadute lo dicono i numeri in testa. */
function DaPassareSuFattureSmart({
  coda,
  aMano,
}: {
  coda: DaPassare[];
  aMano: VersamentoDaFatturare[];
}) {
  const tutte = [...coda, ...aMano];
  const piuVecchio = tutte.length > 0 ? Math.max(...tutte.map((v) => v.giorni_di_attesa)) : 0;
  return (
    <section className="elenco">
      <h2>Incassi da passare su Fatture Smart</h2>
      {tutte.length === 0 ? (
        <p className="aiuto">Nessun incasso da passare su Fatture Smart.</p>
      ) : (
        <p>
          La più vecchia è stata incassata {piuVecchio <= 0 ? "oggi" : `da ${conta(piuVecchio, "giorno", "giorni")}`}.{" "}
          {coda.length > 0 ? (
            <Link to={`/fatturare/fatture-smart?documento=${encodeURIComponent(coda[0]!.documento.identificativo)}`}>Apri la fattura più vecchia.</Link>
          ) : (
            <Link to="/fatturare/fatture-smart">Apri la coda.</Link>
          )}
          {aMano.length > 0 && (
            <>
              {" "}
              Di queste {conta(aMano.length, "è un acconto o un saldo", "sono acconti o saldi")} da fatturare a mano.
            </>
          )}
        </p>
      )}
    </section>
  );
}

function BuchiNumerazione({ buchi, numerati }: { buchi: BucoNumerazione[]; numerati: boolean }) {
  return (
    <section className="elenco">
      <h2>Numerazione</h2>
      {buchi.length === 0 ? (
        <p className={numerati ? "riga-fatto" : "aiuto"}>{numerati ? "Nessun buco rilevato nei numeri registrati." : "Nessun documento numerato da controllare."}</p>
      ) : (
        <>
          <p className="riga-avviso">
            Un buco non è per forza un errore (un numero può essere stato scartato per una
            ragione documentata), ma è sempre una cosa da saper spiegare.
          </p>
          {buchi.map((b) => (
            <p key={`${b.anno}-${b.serie}`} className="riga-avviso">
              Serie {b.serie || "ordinaria"}, {b.anno}: manca{b.numeri.length > 1 ? "no" : ""}{" "}
              il numero{b.numeri.length > 1 ? "i" : ""} {b.numeri.join(", ")}.
            </p>
          ))}
        </>
      )}
    </section>
  );
}
